import { Box, Grid, Paper, Button } from '@mui/material';
import styles from './simulator-v2-contact-history-tab.module.scss';
import { styled } from '@mui/material/styles';
import { useState, useEffect } from 'react';
import SimulatorV2ContactHistoryModal from './simulator-v2-contact-history-modal';
import { contactHistoryListData } from '../../../../../../libs/react/ui-components/src/lib/ccf-contact-history/mock-data/contact-history-mock-data';
import { getSelectedContact } from '../../+state/simulatorV2.slice';
import { useSelector } from 'react-redux';
import {
  CcfChatIcon,
  CcfEmailIcon,
  CcfSmsIcon,
  CcfPhoneIcon,
  CcfInstaIcon,
  CcfOutboundIcon,
  CcfSimulatorV2OutboundPhoneIcon,
  CcfSimulatorV2InboundPhoneIcon,
  CcfEditPenIcon,
  CcfFacebookIcon,
  CcfLinkedinIcon,
  CcfXIcon,
  CcfTrashIcon,
  CcfTooltip,
} from '@nice-ccf/ui-controls';
import { generateDate } from '../../utils/utils';

/* eslint-disable-next-line */
export interface SimulatorV2ContactHistoryTabProps {
  setUpdatedContact: any;
  updatedContact: any;
}

const Item = styled(Paper)(({ theme }) => ({
  textAlign: 'left',
  borderRadius: '0px',
  boxShadow: 'none',
}));

/**
 * used to show the contact History Tab
 * @param props - SimulatorV2ContactHistoryTabProps
 * @example ` <SimulatorV2ContactHistoryTab />`
 */
/**
 * A React functional component that represents the "Contact History" tab in the Simulator V2.
 * This component allows users to view, add, and edit contact history items.
 *
 * @param props - The properties for the `SimulatorV2ContactHistoryTab` component.
 *
 * @remarks
 * - The component uses Redux's `useSelector` to fetch the selected contact data.
 * - It manages multiple states for modals, contact history data, and selected data for editing.
 * - The component renders a list of contact history items, each with options to edit.
 * - Modals are used for adding new items or editing existing ones.
 *
 * @returns A JSX element representing the contact history tab with functionality to manage contact history items.
 *
 * @example
 * ```tsx
 * <SimulatorV2ContactHistoryTab />
 * ```
 */
export function SimulatorV2ContactHistoryTab(
  props: SimulatorV2ContactHistoryTabProps
) {
  const { setUpdatedContact, updatedContact } = props;
  const selectedContact: any = useSelector(getSelectedContact);
  const [showNewItemModal, setShowNewItemModal] = useState(false);
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [contactHistoryData, setContactHistoryData] = useState(
    selectedContact?.contactHistoryList
  );
  const [selectedData, setSelectedData] = useState();

  /**
   * Handles the action of adding a new item by setting the state to display the modal.
   * When invoked, it updates the `showNewItemModal` state to `true`, triggering the modal to appear.
   */
  const handleAddNewItem = () => {
    setShowNewItemModal(true);
  };

  /**
   * Handles the editing of an item by setting the selected data and displaying the edit item modal.
   *
   * @param item - The item to be edited. This can be of any type.
   * @returns void
   */
  const handleEditItem = (item: any): void => {
    setSelectedData(item);
    setShowEditItemModal(true);
  };

  /**
   * Handles the closing of modals in the contact history tab.
   * This function is responsible for hiding both the "New Item" and "Edit Item" modals
   * by updating their respective state variables to `false`.
   */
  const handleClose = () => {
    setShowNewItemModal(false);
    setShowEditItemModal(false);
  };

  useEffect(() => {
    setUpdatedContact({
      ...updatedContact,
      lastUpdatedDate: generateDate(),
      contactHistoryList: contactHistoryData,
    });
    localStorage.setItem(
      'contactHistoryLS',
      JSON.stringify(contactHistoryData)
    );
  }, [contactHistoryData]);

  const mapChannelData = (channelType: any) => {
    switch (channelType) {
      case 'Email':
        return 'KP-Email-Queue';
      case 'Chat':
        return 'KK-Chat-Queue';
      case 'Outbound Voice':
      case 'Inbound Voice':
        return 'Usha-Routing-Queue';
      case 'Instagram':
      case 'X':
      case 'Facebook':
      case 'Linkedin':
        return `KK-${channelType}-Queue`;
      default:
        return '';
    }
  };

  /**
   * Function to handle drawer open close
   * @example renderIconForType()
   */
  const renderIconForType = (param: string) => {
    switch (param && param.toLowerCase()) {
      case 'x':
        return <CcfXIcon />;
      case 'instagram':
        return <CcfInstaIcon />;
      case 'facebook':
        return <CcfFacebookIcon />;
      case 'linkedin':
        return <CcfLinkedinIcon />;
      default:
        return '';
    }
  };
  /**
   * Handles the save operation for a new or existing contact history item.
   *
   * @param newItem - The contact history item to be saved. It can either be a new item or an existing one.
   *   - If `newItem.type` is `'new'`, a new contact history item is created with a unique ID and formatted time.
   *   - If `newItem.type` is not `'new'`, the existing contact history item is updated based on its ID.
   *
   * Updates the contact history data state by either appending a new item or modifying an existing one.
   * Also, closes the modal dialogs for creating or editing items after the operation is complete.
   */
  const handleSave = (newItem: any, type: string) => {
    if (type === 'new') {
      setContactHistoryData((prevContactHistory: any) => {
        const lastId =
          prevContactHistory.length > 0
            ? prevContactHistory[prevContactHistory.length - 1].id
            : 0;
        const modifiedTime = new Date(newItem?.time).toLocaleDateString(
          'en-US'
        );
        const newObj = {
          ...newItem,
          id: lastId + 1,
          time: modifiedTime,
          channel: mapChannelData(newItem?.skill),
        };
        return [...prevContactHistory, newObj];
      });
    } else {
      setContactHistoryData((prevData: any) =>
        prevData.map((item: any) =>
          item.id === newItem.id
            ? { ...newItem, channel: mapChannelData(newItem?.skill) }
            : item
        )
      );
    }
    setShowNewItemModal(false);
    setShowEditItemModal(false);
  };

  /**@example */
  const handleDeleteItem = (item: any, index: number) => {
    setContactHistoryData((prevData: any) => {
      const updatedData = prevData.filter(
        (i: any, idx: number) => idx !== index
      );
      return updatedData;
    });
  };

  return (
    <Box component="section" className={styles.contactHistoryContainer}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={8}>
          <Item className={styles.contactHistoryItems}>Contact History</Item>
        </Grid>
        <Grid item xs={12} sm={12} md={2}>
          <Item className={styles.addNewItem} key="addNewItem">
            <Button
              variant="outlined"
              className={styles.addNewBtn}
              onClick={handleAddNewItem}
            >
              Add New
            </Button>
            {showNewItemModal && (
              <SimulatorV2ContactHistoryModal
                isOpen={showNewItemModal}
                handleClose={handleClose}
                modalHeader="Add New Contact"
                modalData={contactHistoryData}
                modalDataType="new"
                onSave={handleSave}
              />
            )}
          </Item>
        </Grid>
        <Grid item xs={2} sm={2} md={2}></Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        className={styles.contactHistoryDataContainer}
      >
        {contactHistoryData?.length > 0 &&
          contactHistoryData?.map((item: any, index: number) => (
            <Grid item xs={12} sm={12} md={5} key={item.id}>
              <Item className={styles.contactHistoryDataItem}>
                <Grid container spacing={1}>
                  <Grid item xs={1} sm={1} md={1}>
                    {item.skill === 'Email' && <CcfEmailIcon />}
                    {item.skill === 'Chat' && <CcfChatIcon />}
                    {item.skill === 'Outbound Voice' && (
                      <CcfSimulatorV2OutboundPhoneIcon />
                    )}
                    {item.skill === 'Inbound Voice' && (
                      <CcfSimulatorV2InboundPhoneIcon />
                    )}
                    {renderIconForType(item.skill)}
                  </Grid>
                  <Grid item xs={5} sm={5} md={5} className={styles.callNumber}>
                    {item.customer}
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    sm={6}
                    md={6}
                    className={styles.editBtn}
                    key={item.id}
                  >
                    <CcfTooltip title="Delete">
                      <Button
                        style={{ minWidth: '1rem', padding: '0px' }}
                        onClick={() => handleDeleteItem(item, index)}
                      >
                        <CcfTrashIcon
                          style={{ fontSize: '20px' }}
                          fillColor="#000000"
                        />
                      </Button>
                    </CcfTooltip>
                    <CcfTooltip title="Edit">
                      <Button
                        style={{
                          minWidth: '1rem',
                          padding: '0px',
                          height: '1.2rem',
                        }}
                        onClick={() => handleEditItem(item)}
                      >
                        <CcfEditPenIcon
                          viewBox="3 0 20 20"
                          fillColor="#000000"
                        />
                      </Button>
                    </CcfTooltip>
                    {showEditItemModal && (
                      <SimulatorV2ContactHistoryModal
                        isOpen={showEditItemModal}
                        handleClose={handleClose}
                        modalHeader="Edit Contact"
                        modalData={selectedData}
                        modalDataType="edit"
                        onSave={handleSave}
                      />
                    )}
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid xs={12} sm={12} md={12} className={styles.dataItemText}>
                    {new Date(item.time).toLocaleDateString('en-US')}
                  </Grid>
                  <Grid
                    xs={12}
                    sm={12}
                    md={12}
                    className={styles.dataItemAgent}
                  >
                    {item.skill}
                  </Grid>
                  <Grid
                    xs={12}
                    sm={12}
                    md={12}
                    className={
                      item.status === 'Open'
                        ? styles.dataItemStatus
                        : styles.dataItemStatusClosed
                    }
                  >
                    {item.status}
                  </Grid>
                </Grid>
              </Item>
            </Grid>
          ))}
      </Grid>
    </Box>
  );
}

export default SimulatorV2ContactHistoryTab;
